import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { autorun } from 'mobx';

import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import UserContext from '../../../context/UserContext';
import PodContext from '../../../context/PodContext';
import Announcement from '../../Model/Announcement';
import AnnouncementComment from '../../Model/AnnouncementComment';
import AnnouncementCommentReport, { ReportStatus } from '../../Model/AnnouncementCommentReport';
import Community from '../../Model/Community';
import SecondaryContextsReadyContext from '../SecondaryContextsReadyContext';
import { AnnouncementsManagerTab } from '../../routes/announcementsManager/announcementsManagerPages';
import { ALL_COMMUNITIES_FILTER, getPinSlotKey } from '../../utils/announcement';
import {
  DEFAULT_ANNOUNCEMENT_DATE_RANGE,
  buildAnnouncementFeed,
  getSinceDate,
  isRangeCovered,
  mergeAnnouncementDocs,
} from '../../pages/Announcements/feedUtils';

import AnnouncementContext, { initialValues, allPodsOption } from './AnnouncementContext';
import texts from './texts.json';

// Thrown when a deep-linked post can't be shown (missing or soft-removed).
export const ANNOUNCEMENT_UNAVAILABLE_ERROR = 'ANNOUNCEMENT_UNAVAILABLE';

const AnnouncementContextProvider = ({
  children,
}) => {
  const [announcementCollection, setAnnouncementCollection] = useState(initialValues.announcementCollection);
  const [announcementCollectionList, setAnnouncementCollectionList] = useState([]);
  const [pinnedAnnouncementCollection, setPinnedAnnouncementCollection] = useState({ docs: [] });
  const [fetchedSinceDate, setFetchedSinceDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(DEFAULT_ANNOUNCEMENT_DATE_RANGE);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(initialValues.selectedAnnouncement);
  const [selectedAnnouncementCommentsCollection, setSelectedAnnouncementCommentsCollection] = useState(
    initialValues.selectedAnnouncementCommentsCollection,
  );
  const [selectedAnnouncementComments, setSelectedAnnouncementComments] = useState(
    initialValues.selectedAnnouncementComments,
  );
  const [selectedCommentId, setSelectedCommentId] = useState(initialValues.selectedCommentId);
  const [coachCommentsCollection, setCoachCommentsCollection] = useState(initialValues.coachCommentsCollection);
  const [coachUnreadComments, setCoachUnreadComments] = useState(initialValues.coachUnreadComments);
  const [notificationCount, setNotificationCount] = useState(initialValues.notificationCount);
  const [commentReportsCollection, setCommentReportsCollection] = useState(initialValues.commentReportsCollection);
  const [commentReports, setCommentReports] = useState(initialValues.commentReports);
  const [showResolvedReports, setShowResolvedReports] = useState(initialValues.showResolvedReports);
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [isPostsReady, setIsPostsReady] = useState(initialValues.isPostsReady);
  const [isLoading, setIsLoading] = useState(initialValues.isLoading);
  const [isCommentsReady, setIsCommentsReady] = useState(initialValues.isCommentsReady);
  const [selectedPodFilterId, setSelectedPodFilterId] = useState(initialValues.selectedPodFilterId);
  const [isPinConfirmOpen, setIsPinConfirmOpen] = useState(initialValues.isPinConfirmOpen);
  const [pendingPinAnnouncement, setPendingPinAnnouncement] = useState(null);
  const [currentPinnedAnnouncement, setCurrentPinnedAnnouncement] = useState(null);
  const [selectedPublishTab, setSelectedPublishTab] = useState(initialValues.selectedPublishTab);
  const [scheduledAnnouncementCollection, setScheduledAnnouncementCollection] = useState(
    initialValues.scheduledAnnouncementCollection,
  );
  const [communityCollection, setCommunityCollection] = useState(null);
  const [communities, setCommunities] = useState(initialValues.communities);
  const [selectedCommunityFilter, setSelectedCommunityFilter] = useState(
    initialValues.selectedCommunityFilter,
  );
  const [hidePinnedPosts, setHidePinnedPosts] = useState(initialValues.hidePinnedPosts);
  const [showCoachPostsOnly, setShowCoachPostsOnly] = useState(initialValues.showCoachPostsOnly);
  // The open post's sibling docs, one per community it went to, plus which one the view is showing.
  const [selectedGroupSiblings, setSelectedGroupSiblings] = useState(initialValues.selectedGroupSiblings);
  const [selectedSiblingId, setSelectedSiblingId] = useState(initialValues.selectedSiblingId);
  // Scoped to the thread column: unlike isCommentsReady it leaves the post card and the community
  // rail on screen, so a switch doesn't blank the dialog the coach is reading.
  const [isSiblingSwitching, setIsSiblingSwitching] = useState(initialValues.isSiblingSwitching);
  // Comment collections already fetched for this group, so switching back to a tab is free.
  const siblingCommentsCacheRef = useRef(new Map());
  // Bumped per sibling switch, so a slower earlier fetch can't land on a later selection.
  const siblingRequestIdRef = useRef(0);

  const { pods } = useContext(PodContext);
  const { readyForRemainingContexts } = useContext(SecondaryContextsReadyContext);
  const {
    userDoc: {
      id: userId,
    },
  } = useContext(UserContext);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      if (userId) {
        const initialSince = getSinceDate(DEFAULT_ANNOUNCEMENT_DATE_RANGE);
        const [announcementCol, pinnedCol, scheduledCol, communitiesCol] = await Promise.all([
          Announcement.getAnnouncementsByCoach(userId, { sinceDate: initialSince }),
          Announcement.getPinnedAnnouncementsByCoach(userId),
          Announcement.getScheduledAnnouncementsByCoach(userId),
          Community.getByCoach(userId),
        ]);

        if (announcementCol && isComponentMountedRef.current) {
          setAnnouncementCollectionList([announcementCol]);
          setPinnedAnnouncementCollection(pinnedCol);
          setFetchedSinceDate(initialSince);
          setIsPostsReady(true);
        }

        if (scheduledCol && isComponentMountedRef.current) {
          setScheduledAnnouncementCollection(scheduledCol);
        }

        if (communitiesCol && isComponentMountedRef.current) {
          setCommunityCollection(communitiesCol);
        }

        // Fetch unread comments and comment reports separately in parallel.
        // Doing so ensures that loading these (potentially slow) secondary
        // resources does not block the main context initialization,
        // allowing the Announcement context to become ready as soon as announcement data is available.
        const [unreadCommentsCollection, commentReportsCol] = await Promise.all([
          AnnouncementComment.getUnreadCommentsOfCoach(userId),
          AnnouncementCommentReport.getAnnouncementCommentReportsOfCoach(userId, false),
        ]);

        if (isComponentMountedRef.current) {
          setCoachCommentsCollection(unreadCommentsCollection);
          setCommentReportsCollection(commentReportsCol);
          setIsReady(true);
        }
      }
    };

    // Initialize the context only when the primary and secondary contexts are ready.
    if (!isReady && readyForRemainingContexts) {
      init();
    }
  }, [
    userId,
    isReady,
    isComponentMountedRef,
    readyForRemainingContexts,
  ]);

  // When the selected range widens beyond what we've fetched, fetch only the
  // missing older segment and append it. Narrowing/repeat selections fetch nothing.
  useEffect(() => {
    if (!userId || !isPostsReady) {
      return undefined;
    }
    if (isRangeCovered(selectedDateRange, fetchedSinceDate)) {
      setIsLoadingPosts(false);
      return undefined;
    }
    let cancelled = false;
    const expandRange = async () => {
      setIsLoadingPosts(true);
      const targetSince = getSinceDate(selectedDateRange);
      try {
        const gapCollection = await Announcement.getAnnouncementsByCoach(userId, {
          sinceDate: targetSince,
          untilDate: fetchedSinceDate,
        });
        if (!cancelled && isComponentMountedRef.current) {
          setAnnouncementCollectionList((prev) => [...prev, gapCollection]);
          setFetchedSinceDate(targetSince);
        }
      } catch {
        if (!cancelled && isComponentMountedRef.current) {
          showToast(texts.loadPostsError, { error: true });
        }
      } finally {
        if (!cancelled && isComponentMountedRef.current) {
          setIsLoadingPosts(false);
        }
      }
    };
    expandRange();
    return () => {
      cancelled = true;
    };
  }, [
    userId,
    isPostsReady,
    selectedDateRange,
    fetchedSinceDate,
    isComponentMountedRef,
    showToast,
  ]);

  // Flatten the date-bounded segments + the unbounded pinned set into the display-ready collection.
  useEffect(() => {
    const disposer = autorun(() => {
      const dateDocs = announcementCollectionList.flatMap((collection) => collection.docs || []);
      const pinnedDocs = pinnedAnnouncementCollection.docs || [];
      const since = getSinceDate(selectedDateRange);
      setAnnouncementCollection({ docs: mergeAnnouncementDocs(dateDocs, pinnedDocs, since) });
    });
    return () => disposer();
  }, [
    announcementCollectionList,
    pinnedAnnouncementCollection,
    selectedDateRange,
  ]);

  // Keep the community list live off the observed collection so create/rename/archive
  // (from the Communities tab) reflect automatically in the filter/recipients.
  useEffect(() => {
    if (!communityCollection) {
      return undefined;
    }
    const disposer = autorun(() => {
      setCommunities(communityCollection.docs.filter((doc) => doc.data));
    });
    return () => disposer();
  }, [communityCollection]);

  const isCommunityNameTaken = useCallback((name, excludeId = null) => {
    const normalized = (name || '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    return communities.some(
      (community) => community.id !== excludeId && (community.name || '').trim().toLowerCase() === normalized,
    );
  }, [communities]);

  useEffect(() => {
    const disposer = autorun(() => {
      const sortedCoachComments = coachCommentsCollection?.docs?.slice().sort(
        (a, b) => new Date(b.displayDate.toDate()) - new Date(a.displayDate.toDate()),
      );
      // This autorun fires on community-wide unread-comment snapshots. Skip the state
      // update (and the resulting context-wide re-render) when the set is unchanged.
      setCoachUnreadComments((prev) => (
        (prev.length === sortedCoachComments.length
          && prev.every((c, i) => c.id === sortedCoachComments[i].id))
          ? prev
          : sortedCoachComments
      ));
      setNotificationCount((prev) => (
        prev[AnnouncementsManagerTab.NOTIFICATIONS] === sortedCoachComments.length
          ? prev
          : { ...prev, [AnnouncementsManagerTab.NOTIFICATIONS]: sortedCoachComments.length }
      ));
    });
    return () => disposer();
  }, [coachCommentsCollection]);

  useEffect(() => {
    const disposer = autorun(() => {
      const sortedCommentReports = commentReportsCollection?.docs?.slice().sort(
        (a, b) => new Date(b.reportedAt) - new Date(a.reportedAt),
      );
      const filteredCommentReports = sortedCommentReports.filter((report) => report.status === ReportStatus.OPEN);
      setCommentReports(sortedCommentReports);
      setNotificationCount((prev) => ({
        ...prev,
        [AnnouncementsManagerTab.REPORTS]: filteredCommentReports.length,
      }));
    });
    return () => disposer();
  }, [commentReportsCollection]);

  useEffect(() => {
    setIsCommentsReady(false);
    setSelectedPodFilterId(allPodsOption.value);
    const disposer = autorun(() => {
      const sortedComments = selectedAnnouncementCommentsCollection?.docs?.slice().sort(
        (a, b) => new Date(b.displayDate.toDate()) - new Date(a.displayDate.toDate()),
      );
      setSelectedAnnouncementComments(sortedComments);
      setIsCommentsReady(true);
    });
    return () => disposer();
  }, [selectedAnnouncementCommentsCollection]);

  const updateCommentAndAnnouncementState = useCallback(async (announcement, commentId) => {
    setIsCommentsReady(false);
    setSelectedAnnouncement(announcement);
    setSelectedCommentId(commentId);
    siblingCommentsCacheRef.current = new Map();
    // Retires any sibling switch still in flight from the post being closed.
    siblingRequestIdRef.current += 1;
    setIsSiblingSwitching(false);

    if (!announcement) {
      setSelectedAnnouncementCommentsCollection({ docs: [] });
      setSelectedGroupSiblings([]);
      setSelectedSiblingId(null);
      setIsCommentsReady(true);
      return;
    }

    setSelectedSiblingId(announcement.id);

    // A post sent to several communities keeps a separate thread per community, so load the group
    // alongside the comments and let the view offer one row per sibling. A post that went to a
    // single community needs no second read - getActiveGroupSiblings short-circuits on it.
    const [siblings, commentsCol] = await Promise.all([
      Announcement.getActiveGroupSiblings(announcement).catch(() => [announcement]),
      AnnouncementComment.getAnnouncementComments(announcement.id),
    ]);

    if (isComponentMountedRef.current) {
      setSelectedGroupSiblings(siblings);
      siblingCommentsCacheRef.current.set(announcement.id, commentsCol);
      setSelectedAnnouncementCommentsCollection(commentsCol);
      setIsCommentsReady(true);
    }
  }, [isComponentMountedRef]);

  // `getGroupSiblings` fetches a detached collection, and its docs disable observer refs, so they
  // never see a like or comment land. Swap in the feed's own doc wherever the group member is
  // loaded there, so the card and the rail counts keep tracking after a switch.
  const liveGroupSiblings = selectedGroupSiblings.map(
    (sibling) => announcementCollection.docs?.find((doc) => doc.id === sibling.id) || sibling,
  );

  /**
   * Switches the open post to one of its siblings, i.e. to another community's copy and thread.
   * @param {string} siblingId
   */
  const selectGroupSibling = useCallback(async (siblingId) => {
    const sibling = liveGroupSiblings.find((doc) => doc.id === siblingId);
    if (!sibling || siblingId === selectedSiblingId) {
      return;
    }

    const previousSibling = liveGroupSiblings.find((doc) => doc.id === selectedSiblingId);

    // Bumped on every switch, cached ones included, so a fetch still in flight for the community
    // being left can't put a stale thread under the rail or clear a spinner that isn't its own.
    siblingRequestIdRef.current += 1;
    const requestId = siblingRequestIdRef.current;
    const isStale = () => !isComponentMountedRef.current || siblingRequestIdRef.current !== requestId;

    setSelectedSiblingId(siblingId);
    setSelectedAnnouncement(sibling);
    // A comment deep link belongs to the sibling it was raised on, not to the one being opened.
    setSelectedCommentId(null);

    const cached = siblingCommentsCacheRef.current.get(siblingId);
    if (cached) {
      setSelectedAnnouncementCommentsCollection(cached);
      setIsSiblingSwitching(false);
      return;
    }

    setIsSiblingSwitching(true);
    try {
      const commentsCol = await AnnouncementComment.getAnnouncementComments(siblingId);
      if (isStale()) {
        return;
      }
      siblingCommentsCacheRef.current.set(siblingId, commentsCol);
      setSelectedAnnouncementCommentsCollection(commentsCol);
    } catch {
      if (isStale()) {
        return;
      }
      // Put the coach back on the thread they were reading rather than on an empty one.
      if (previousSibling) {
        setSelectedSiblingId(previousSibling.id);
        setSelectedAnnouncement(previousSibling);
      }
      showToast(texts.switchCommunityError, { error: true });
    } finally {
      if (!isStale()) {
        setIsSiblingSwitching(false);
      }
    }
  }, [liveGroupSiblings, selectedSiblingId, isComponentMountedRef, showToast]);

  const onShowResolvedReports = useCallback(async (shouldShowResolvedReports) => {
    setIsLoading(true);
    setShowResolvedReports(shouldShowResolvedReports);
    const commentReportsCol = await AnnouncementCommentReport.getAnnouncementCommentReportsOfCoach(
      userId,
      shouldShowResolvedReports,
    );
    if (isComponentMountedRef.current) {
      setCommentReportsCollection(commentReportsCol);
      setIsLoading(false);
    }
  }, [isComponentMountedRef, userId]);

  // Pod filtering logic
  const uniquePodIdsOfComments = useMemo(() => {
    const podIds = new Set();
    selectedAnnouncementComments.forEach((comment) => {
      const { podId } = comment;
      if (podId && podId !== allPodsOption.value) {
        podIds.add(podId);
      }
    });
    return [...podIds];
  }, [selectedAnnouncementComments]);

  const podFilterOptions = useMemo(() => {
    if (uniquePodIdsOfComments.length === 0) {
      return {};
    }

    const podIdSet = new Set(uniquePodIdsOfComments);
    const filterOptions = { [allPodsOption.value]: allPodsOption.label };

    pods.forEach((pod) => {
      if (podIdSet.has(pod.id)) {
        filterOptions[pod.id] = pod.name;
      }
    });

    return filterOptions;
  }, [uniquePodIdsOfComments, pods]);

  const showPodFilter = useMemo(
    () => Object.keys(podFilterOptions).length > 1,
    [podFilterOptions],
  );

  const filteredComments = useMemo(() => {
    if (selectedPodFilterId === allPodsOption.value) {
      return selectedAnnouncementComments;
    }
    return selectedAnnouncementComments.filter((comment) => {
      const { podId } = comment;
      return podId === selectedPodFilterId;
    });
  }, [selectedAnnouncementComments, selectedPodFilterId]);

  const handlePinRequest = useCallback(async (announcement) => {
    try {
      const announcementSlotKey = getPinSlotKey(announcement);
      const existingPin = (announcementCollection?.docs || [])
        .filter((p) => p.isPinned)
        .find((p) => getPinSlotKey(p) === announcementSlotKey);
      if (!existingPin) {
        await announcement.pin(userId);
        showToast(texts.pinSuccess);
      } else if (existingPin.id !== announcement.id) {
        setCurrentPinnedAnnouncement(existingPin);
        setPendingPinAnnouncement(announcement);
        setIsPinConfirmOpen(true);
      }
    } catch {
      showToast(texts.pinError, { error: true });
    }
  }, [announcementCollection, userId, showToast]);

  const handleUnpinRequest = useCallback(async (announcement) => {
    try {
      await announcement.unpin(userId);
      showToast(texts.unpinSuccess);
    } catch {
      showToast(texts.unpinError, { error: true });
    }
  }, [userId, showToast]);

  const cancelPinReplace = useCallback(() => {
    setIsPinConfirmOpen(false);
    setPendingPinAnnouncement(null);
    setCurrentPinnedAnnouncement(null);
  }, []);

  const confirmPinReplace = useCallback(async () => {
    if (!currentPinnedAnnouncement || !pendingPinAnnouncement) return;
    try {
      await Announcement.replacePinnedPost(currentPinnedAnnouncement, pendingPinAnnouncement, userId);
      cancelPinReplace();
      showToast(texts.pinReplaceSuccess);
    } catch {
      showToast(texts.pinReplaceError, { error: true });
    }
  }, [currentPinnedAnnouncement, pendingPinAnnouncement, userId, showToast, cancelPinReplace]);

  // The Scheduled tab lists one card per post, not one per community copy, so the badge counts posts
  // - five communities is five docs but one entry. Deliberately not scoped to the sidebar's
  // community filter: the badge is read from the Published tab, where a total silenced by a control
  // further down the page would just look like nothing is scheduled.
  // Read during render rather than memoised, so the observed collection still tracks.
  const scheduledCount = buildAnnouncementFeed(scheduledAnnouncementCollection?.docs ?? [], {
    communityFilter: ALL_COMMUNITIES_FILTER,
  }).length;

  const updateCommentAndAnnouncementStateById = useCallback(async (announcementId, commentId) => {
    let announcement = announcementCollection.docs.find((doc) => doc.id === announcementId);
    if (!announcement) {
      // Could be older than the loaded window — fall back to a single-doc fetch.
      announcement = await Announcement.getAnnouncementById(announcementId);
      if (!announcement.exists || announcement.isRemoved) {
        const error = new Error(texts.announcementNotFound);
        error.code = ANNOUNCEMENT_UNAVAILABLE_ERROR;
        throw error;
      }
    }
    await updateCommentAndAnnouncementState(announcement, commentId);
  }, [announcementCollection, updateCommentAndAnnouncementState]);

  const context = useMemo(() => ({
    announcementCollection,
    isReady,
    selectedAnnouncement,
    selectedAnnouncementComments,
    isCommentsReady,
    updateCommentAndAnnouncementState,
    selectedCommentId,
    coachUnreadComments,
    notificationCount,
    commentReports,
    showResolvedReports,
    onShowResolvedReports,
    isLoading,
    isPostsReady,
    podFilterOptions,
    showPodFilter,
    selectedPodFilterId,
    setSelectedPodFilterId,
    filteredComments,
    isPinConfirmOpen,
    currentPinnedAnnouncement,
    handlePinRequest,
    handleUnpinRequest,
    confirmPinReplace,
    cancelPinReplace,
    selectedPublishTab,
    setSelectedPublishTab,
    scheduledAnnouncementCollection,
    scheduledCount,
    selectedDateRange,
    setSelectedDateRange,
    isLoadingPosts,
    updateCommentAndAnnouncementStateById,
    communities,
    isCommunityNameTaken,
    selectedCommunityFilter,
    setSelectedCommunityFilter,
    hidePinnedPosts,
    setHidePinnedPosts,
    showCoachPostsOnly,
    setShowCoachPostsOnly,
    selectedGroupSiblings: liveGroupSiblings,
    selectedSiblingId,
    selectGroupSibling,
    isSiblingSwitching,
  }), [
    announcementCollection,
    isReady,
    selectedAnnouncement,
    selectedAnnouncementComments,
    isCommentsReady,
    updateCommentAndAnnouncementState,
    selectedCommentId,
    coachUnreadComments,
    notificationCount,
    commentReports,
    showResolvedReports,
    onShowResolvedReports,
    isLoading,
    isPostsReady,
    podFilterOptions,
    showPodFilter,
    selectedPodFilterId,
    filteredComments,
    isPinConfirmOpen,
    currentPinnedAnnouncement,
    handlePinRequest,
    handleUnpinRequest,
    confirmPinReplace,
    cancelPinReplace,
    selectedPublishTab,
    setSelectedPublishTab,
    scheduledAnnouncementCollection,
    scheduledCount,
    selectedDateRange,
    setSelectedDateRange,
    isLoadingPosts,
    updateCommentAndAnnouncementStateById,
    communities,
    isCommunityNameTaken,
    selectedCommunityFilter,
    hidePinnedPosts,
    showCoachPostsOnly,
    liveGroupSiblings,
    selectedSiblingId,
    selectGroupSibling,
    isSiblingSwitching,
  ]);

  return (
    <AnnouncementContext.Provider value={context}>
      {children}
    </AnnouncementContext.Provider>
  );
};

AnnouncementContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(AnnouncementContextProvider);
